import { type ReactElement, useMemo } from 'react';

import type {
  FilesFileSchemaWithId,
  FilesFileSetSchemaWithIdNullable,
  FilesFormatSchemaWithId,
} from '@iconik-io/iconik-api/src/services/files';

import { mapLegacyIconName } from '../../icons/utils';
import OpenContextMenuButton from '../../ui/table/open_context_menu_button/open_context_menu_button';
import {
  DownloadOpenFileSets,
  FileFormatLink,
  FileSets,
  FormatName,
  Storage,
} from './cell_components';
import ContextMenu from './context_menu_formats';
import TableGroup from './table_group';
import type { FilesGridColumn, FormatProgress } from './types';

type TableFormatsProps = {
  items: FilesFormatSchemaWithId[];
  files: FilesFileSchemaWithId[];
  canDownload: boolean;
  asset: string;
  onShowFilesClick: (fileSet: FilesFileSetSchemaWithIdNullable) => void;
  title: string;
  uploading: FormatProgress;
  help: string;
  uiKey: string;
  selectedIds: string[];
  onSelectIds: (ids: string[]) => void;
  readOnly?: boolean;
  mediaType?: string;
};

type ColumnProps = Pick<
  TableFormatsProps,
  | 'mediaType'
  | 'uploading'
  | 'files'
  | 'canDownload'
  | 'asset'
  | 'readOnly'
  | 'onShowFilesClick'
>;

const getColumns = (
  props: ColumnProps,
): FilesGridColumn<FilesFormatSchemaWithId>[] => [
  {
    name: 'name',
    component: ({ item }) => (
      <FormatName
        format={item}
        mediaType={props.mediaType}
        progress={props.uploading[item.id]}
      />
    ),
  },
  {
    name: 'asset_id',
    component: ({ item }) => (
      <FileSets onClick={props.onShowFilesClick} format={item} />
    ),
  },
  {
    name: 'storage_methods',
    component: ({ item }) => (
      <Storage format={item} readOnly={props.readOnly} />
    ),
  },
  {
    name: 'status',
    component: ({ item }) => {
      const externalReference = item.external_references?.[0];
      if (externalReference) {
        return (
          <FileFormatLink
            iconName={mapLegacyIconName(externalReference?.icon) ?? 'open'}
            uri={externalReference.uri}
          />
        );
      }
      if (!props.readOnly) {
        return (
          <DownloadOpenFileSets
            canDownload={props.canDownload}
            format={item}
            files={props.files}
            assetId={props.asset}
          />
        );
      }
      return null;
    },
  },
  {
    name: 'menu',
    component: () => <OpenContextMenuButton />,
  },
];

export default function TableFormats(
  props: TableFormatsProps,
): ReactElement | null {
  const {
    items,
    title,
    help,
    uiKey,
    mediaType,
    uploading,
    files,
    canDownload,
    asset,
    readOnly,
    onShowFilesClick,
  } = props;

  const columns = useMemo(
    () =>
      getColumns({
        mediaType,
        uploading,
        files,
        canDownload,
        asset,
        readOnly,
        onShowFilesClick,
      }),
    [
      mediaType,
      uploading,
      files,
      canDownload,
      asset,
      readOnly,
      onShowFilesClick,
    ],
  );

  if (!items.length) {
    return null;
  }

  return (
    <TableGroup<FilesFormatSchemaWithId>
      title={title}
      items={items}
      contextMenu={ContextMenu}
      columns={columns}
      help={help}
      uiKey={uiKey}
      selectedIds={props.selectedIds}
      onSelectIds={props.onSelectIds}
    />
  );
}
